/**
 * Machine API
 * 
 * API Guide — Machine Management
 */
import axios from "axios";

const API_URL = "machine";

/**
 * Get all machines with optional filters
 * @param {Object} params - Query parameters (type, status, is_active)
 */
export const getAllMachines = async (params = {}) => {
  const response = await axios.get(API_URL, { params });
  return response.data;
};

/**
 * Get machines by type
 * @param {string} type - Machine type (corrugation, die_cutting, rotary, slotter, offset, etc.)
 */
export const getMachinesByType = async (type) => {
  const response = await axios.get(`${API_URL}/type/${type}`);
  return response.data;
};

/**
 * Get single machine by ID
 * @param {string} id - Machine ID
 */
export const getMachine = async (id) => {
  const response = await axios.get(`${API_URL}/${id}`);
  return response.data;
};

/**
 * Create new machine
 * @param {Object} data - Machine data
 */
export const createMachine = async (data) => {
  const response = await axios.post(API_URL, data);
  return response.data;
};

/**
 * Update machine
 * @param {string} id - Machine ID
 * @param {Object} data - Updated machine data
 */
export const updateMachine = async (id, data) => {
  const response = await axios.put(`${API_URL}/${id}`, data);
  return response.data;
};

/**
 * Delete machine (soft delete)
 * @param {string} id - Machine ID
 */
export const deleteMachine = async (id) => {
  const response = await axios.delete(`${API_URL}/${id}`);
  return response.data;
};

/**
 * Update machine status
 * @param {string} id - Machine ID
 * @param {string} status - New status (operational, maintenance, breakdown)
 * @param {string} reason - Reason for status change
 */
export const updateMachineStatus = async (id, status, reason) => {
  const response = await axios.put(`${API_URL}/${id}/status`, { status, reason });
  return response.data;
};

/**
 * Get machines that need maintenance
 */
export const getMaintenanceDue = async () => {
  const response = await axios.get(`${API_URL}/maintenance-due`);
  return response.data;
};

/**
 * Get machine utilization
 * @param {string} id - Machine ID
 * @param {Object} params - Query parameters (start_date, end_date)
 */
export const getMachineUtilization = async (id, params = {}) => {
  const response = await axios.get(`${API_URL}/${id}/utilization`, { params });
  return response.data;
};

/**
 * Get maintenance logs for a machine
 * @param {string} id - Machine ID
 * @param {Object} params - Query parameters (type, limit)
 */
export const getMaintenanceLogs = async (id, params = {}) => {
  const response = await axios.get(`${API_URL}/${id}/maintenance`, { params });
  return response.data;
};

/**
 * Log maintenance activity
 * @param {string} id - Machine ID
 * @param {Object} data - Maintenance data
 */
export const logMaintenance = async (id, data) => {
  const response = await axios.post(`${API_URL}/${id}/maintenance`, data);
  return response.data;
};

/**
 * Complete maintenance log
 * @param {string} id - Machine ID
 * @param {string} logId - Maintenance log ID
 */
export const completeMaintenance = async (id, logId) => {
  const response = await axios.put(`${API_URL}/${id}/maintenance/${logId}/complete`);
  return response.data;
};

/**
 * Set machine capacity
 * @param {string} id - Machine ID
 * @param {Object} data - Capacity data
 */
export const setMachineCapacity = async (id, data) => {
  const response = await axios.put(`${API_URL}/${id}/capacity`, data);
  return response.data;
};

/**
 * Get operators for a machine
 * @param {string} id - Machine ID
 */
export const getMachineOperators = async (id) => {
  const response = await axios.get(`${API_URL}/${id}/operators`);
  return response.data;
};

/**
 * Assign operator to machine
 * @param {string} id - Machine ID
 * @param {Object} data - Operator assignment data
 */
export const assignOperator = async (id, data) => {
  const response = await axios.post(`${API_URL}/${id}/operators`, data);
  return response.data;
};

/**
 * Remove operator from machine
 * @param {string} id - Machine ID
 * @param {string} userId - User ID to remove
 */
export const removeOperator = async (id, userId) => {
  const response = await axios.delete(`${API_URL}/${id}/operators/${userId}`);
  return response.data;
};

/**
 * Get all vendors (suppliers with is_vendor=true) for machine outsourcing
 */
export const getVendors = async () => {
  const response = await axios.get("supplier?status=true");
  // Filter to only include vendors or return all suppliers
  const suppliers = Array.isArray(response.data) ? response.data : response.data?.getAllSupplier || [];
  return suppliers;
};

export default {
  getAllMachines,
  getMachinesByType,
  getMachine,
  createMachine,
  updateMachine,
  deleteMachine,
  updateMachineStatus,
  getMaintenanceDue,
  getMachineUtilization,
  getMaintenanceLogs,
  logMaintenance,
  completeMaintenance,
  setMachineCapacity,
  getMachineOperators,
  assignOperator,
  removeOperator,
  getVendors,
};
